/**
 * State abbreviations for each US State + territories.
 *
 * See also: https://en.wikipedia.org/wiki/List_of_U.S._state_abbreviations
 */
export const USState = {
  AL: "AL",
  AK: "AK",
  AZ: "AZ",
  AR: "AR",
  CA: "CA",
  CO: "CO",
  CT: "CT",
  DE: "DE",
  DC: "DC",
  FL: "FL",
  GA: "GA",
  HI: "HI",
  ID: "ID",
  IL: "IL",
  IN: "IN",
  IA: "IA",
  KS: "KS",
  KY: "KY",
  LA: "LA",
  ME: "ME",
  MD: "MD",
  MA: "MA",
  MI: "MI",
  MN: "MN",
  MS: "MS",
  MO: "MO",
  MT: "MT",
  NE: "NE",
  NV: "NV",
  NH: "NH",
  NJ: "NJ",
  NM: "NM",
  NY: "NY",
  NC: "NC",
  ND: "ND",
  OH: "OH",
  OK: "OK",
  OR: "OR",
  PA: "PA",
  RI: "RI",
  SC: "SC",
  SD: "SD",
  TN: "TN",
  TX: "TX",
  UT: "UT",
  VT: "VT",
  VA: "VA",
  WA: "WA",
  WV: "WV",
  WI: "WI",
  WY: "WY",
  AS: "AS",
  GU: "GU",
  MP: "MP",
  PR: "PR",
  VI: "VI",
} as const;

export type USState = (typeof USState)[keyof typeof USState];

/**
 * Maps USState abbreviations to full names.
 */
export const US_STATE_ABBR_TO_NAME: Record<USState, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AS: "American Samoa",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District Of Columbia",
  FL: "Florida",
  GA: "Georgia",
  GU: "Guam",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  MP: "Northern Mariana Islands",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  PR: "Puerto Rico",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VI: "Virgin Islands",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export type StateItem = {
  abbreviation: USState;
  menuItemLabel: string;
};

/**
 * Pre-computed array of all US state items for the searchable state combobox.
 * Listed in the same display order used by the legacy mindreader3 component.
 */
export const STATE_ITEMS: StateItem[] = [
  { abbreviation: USState.AL, menuItemLabel: "AL - Alabama" },
  { abbreviation: USState.AK, menuItemLabel: "AK - Alaska" },
  { abbreviation: USState.AZ, menuItemLabel: "AZ - Arizona" },
  { abbreviation: USState.AR, menuItemLabel: "AR - Arkansas" },
  { abbreviation: USState.CA, menuItemLabel: "CA - California" },
  { abbreviation: USState.CO, menuItemLabel: "CO - Colorado" },
  { abbreviation: USState.CT, menuItemLabel: "CT - Connecticut" },
  { abbreviation: USState.DE, menuItemLabel: "DE - Delaware" },
  { abbreviation: USState.DC, menuItemLabel: "DC - District Of Columbia" },
  { abbreviation: USState.FL, menuItemLabel: "FL - Florida" },
  { abbreviation: USState.GA, menuItemLabel: "GA - Georgia" },
  { abbreviation: USState.HI, menuItemLabel: "HI - Hawaii" },
  { abbreviation: USState.ID, menuItemLabel: "ID - Idaho" },
  { abbreviation: USState.IL, menuItemLabel: "IL - Illinois" },
  { abbreviation: USState.IN, menuItemLabel: "IN - Indiana" },
  { abbreviation: USState.IA, menuItemLabel: "IA - Iowa" },
  { abbreviation: USState.KS, menuItemLabel: "KS - Kansas" },
  { abbreviation: USState.KY, menuItemLabel: "KY - Kentucky" },
  { abbreviation: USState.LA, menuItemLabel: "LA - Louisiana" },
  { abbreviation: USState.ME, menuItemLabel: "ME - Maine" },
  { abbreviation: USState.MD, menuItemLabel: "MD - Maryland" },
  { abbreviation: USState.MA, menuItemLabel: "MA - Massachusetts" },
  { abbreviation: USState.MI, menuItemLabel: "MI - Michigan" },
  { abbreviation: USState.MN, menuItemLabel: "MN - Minnesota" },
  { abbreviation: USState.MS, menuItemLabel: "MS - Mississippi" },
  { abbreviation: USState.MO, menuItemLabel: "MO - Missouri" },
  { abbreviation: USState.MT, menuItemLabel: "MT - Montana" },
  { abbreviation: USState.NE, menuItemLabel: "NE - Nebraska" },
  { abbreviation: USState.NV, menuItemLabel: "NV - Nevada" },
  { abbreviation: USState.NH, menuItemLabel: "NH - New Hampshire" },
  { abbreviation: USState.NJ, menuItemLabel: "NJ - New Jersey" },
  { abbreviation: USState.NM, menuItemLabel: "NM - New Mexico" },
  { abbreviation: USState.NY, menuItemLabel: "NY - New York" },
  { abbreviation: USState.NC, menuItemLabel: "NC - North Carolina" },
  { abbreviation: USState.ND, menuItemLabel: "ND - North Dakota" },
  { abbreviation: USState.OH, menuItemLabel: "OH - Ohio" },
  { abbreviation: USState.OK, menuItemLabel: "OK - Oklahoma" },
  { abbreviation: USState.OR, menuItemLabel: "OR - Oregon" },
  { abbreviation: USState.PA, menuItemLabel: "PA - Pennsylvania" },
  { abbreviation: USState.RI, menuItemLabel: "RI - Rhode Island" },
  { abbreviation: USState.SC, menuItemLabel: "SC - South Carolina" },
  { abbreviation: USState.SD, menuItemLabel: "SD - South Dakota" },
  { abbreviation: USState.TN, menuItemLabel: "TN - Tennessee" },
  { abbreviation: USState.TX, menuItemLabel: "TX - Texas" },
  { abbreviation: USState.UT, menuItemLabel: "UT - Utah" },
  { abbreviation: USState.VT, menuItemLabel: "VT - Vermont" },
  { abbreviation: USState.VA, menuItemLabel: "VA - Virginia" },
  { abbreviation: USState.WA, menuItemLabel: "WA - Washington" },
  { abbreviation: USState.WV, menuItemLabel: "WV - West Virginia" },
  { abbreviation: USState.WI, menuItemLabel: "WI - Wisconsin" },
  { abbreviation: USState.WY, menuItemLabel: "WY - Wyoming" },
  { abbreviation: USState.AS, menuItemLabel: "AS - American Samoa" },
  { abbreviation: USState.GU, menuItemLabel: "GU - Guam" },
  { abbreviation: USState.MP, menuItemLabel: "MP - Northern Mariana Islands" },
  { abbreviation: USState.PR, menuItemLabel: "PR - Puerto Rico" },
  { abbreviation: USState.VI, menuItemLabel: "VI - Virgin Islands" },
];
